const MAX_TAG_LEN = 30;
const MAX_TAGS = 10;

export function sanitizeTag(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").replace(/^-+|-+$/g, "").slice(0, MAX_TAG_LEN);
}

export function sanitizeTags(raw: string[]): string[] {
  const out: string[] = [];
  for (const r of raw) {
    const t = sanitizeTag(r);
    if (t && !out.includes(t) && out.length < MAX_TAGS) out.push(t);
  }
  return out;
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;"));
}

// A pill/typeahead tag editor mounted into `host`. `suggestions` is the pool of
// existing tags to autocomplete against. Read the current value via getTags().
export interface TagInput { getTags(): string[]; }

export function mountTagInput(host: HTMLElement, initial: string[], suggestions: string[]): TagInput {
  let tags = sanitizeTags(initial);
  // Sanitize suggestions so innerHTML interpolation is structurally safe (not just escaped).
  const safeSuggestions = sanitizeTags(suggestions);
  host.classList.add("taginput");
  const render = () => {
    host.innerHTML =
      tags.map((t) => `<span class="pill" data-tag="${esc(t)}">${esc(t)}<span class="pill-x" data-tag="${esc(t)}">&times;</span></span>`).join("") +
      `<input type="text" class="taginput-field" placeholder="Add a tag…" />` +
      `<div class="taginput-suggest" hidden></div>`;
    const field = host.querySelector<HTMLInputElement>(".taginput-field")!;
    const sugg = host.querySelector<HTMLDivElement>(".taginput-suggest")!;
    for (const x of host.querySelectorAll<HTMLElement>(".pill-x")) {
      x.addEventListener("click", () => { tags = tags.filter((t) => t !== x.dataset.tag); render(); });
    }
    const commit = (raw: string) => {
      const t = sanitizeTag(raw);
      if (t && !tags.includes(t) && tags.length < MAX_TAGS) tags.push(t);
      render();
      host.querySelector<HTMLInputElement>(".taginput-field")!.focus();
    };
    const showSuggest = () => {
      const q = sanitizeTag(field.value);
      const matches = q ? safeSuggestions.filter((s) => s.includes(q) && !tags.includes(s)).slice(0, 6) : [];
      if (!matches.length) { sugg.hidden = true; return; }
      sugg.hidden = false;
      sugg.innerHTML = matches.map((m) => `<div class="taginput-opt" data-tag="${esc(m)}">${esc(m)}</div>`).join("");
      for (const o of sugg.querySelectorAll<HTMLElement>(".taginput-opt")) {
        o.addEventListener("mousedown", (e) => { e.preventDefault(); commit(o.dataset.tag!); });
      }
    };
    field.addEventListener("input", showSuggest);
    field.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(field.value); }
      else if (e.key === "Backspace" && !field.value && tags.length) { tags.pop(); render(); }
    });
    field.addEventListener("blur", () => { setTimeout(() => { sugg.hidden = true; }, 120); });
  };
  render();
  return { getTags: () => [...tags] };
}
